/**
 * Default gas end-use shares (fractions of sector gas use, sum = 1).
 * Used to limit building electrification to space + water heating (electrifiable pool).
 */

export interface ResidentialGasEndUseShares {
  spaceHeating: number;
  waterHeating: number;
  cooking: number;
  clothesDrying: number;
}

export interface CommercialGasEndUseShares {
  spaceHeating: number;
  waterHeating: number;
  cooking: number;
  other: number;
}

/** User-facing / scenario payload (fractions 0–1, need not sum to 1 — will normalize). */
export interface BuildingEndUseAssumptionsInput {
  residential?: Partial<ResidentialGasEndUseShares>;
  commercial?: Partial<CommercialGasEndUseShares>;
}

export interface BuildingEndUseAssumptions extends BuildingEndUseAssumptionsInput {
  residential: ResidentialGasEndUseShares;
  commercial: CommercialGasEndUseShares;
}

/**
 * Default residential gas end-use shares (Building Electrification policy).
 * Target mix: Space heating 31%, Water heating 35%, Other 34%.
 * "Other" (34%) is split between cooking and clothes drying using the prior 7:3 ratio between those two.
 */
export const DEFAULT_RESIDENTIAL_GAS_END_USE: ResidentialGasEndUseShares = {
  spaceHeating: 0.31,
  waterHeating: 0.35,
  cooking: 0.238,
  clothesDrying: 0.102,
};

export const DEFAULT_COMMERCIAL_GAS_END_USE: CommercialGasEndUseShares = {
  spaceHeating: 0.5,
  waterHeating: 0.2,
  cooking: 0.2,
  other: 0.1,
};

/** Resolved shares + derived electrifiable fractions (space + water). */
export interface ResolvedBuildingEndUseAssumptions {
  residential: ResidentialGasEndUseShares;
  commercial: CommercialGasEndUseShares;
  electrifiableResidential: number;
  electrifiableCommercial: number;
}

function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

function normalize4(a: number, b: number, c: number, d: number): ResidentialGasEndUseShares {
  const sum = Math.max(1e-12, a + b + c + d);
  return {
    spaceHeating: clamp01(a / sum),
    waterHeating: clamp01(b / sum),
    cooking: clamp01(c / sum),
    clothesDrying: clamp01(d / sum),
  };
}

function normalizeCommercial4(a: number, b: number, c: number, d: number): CommercialGasEndUseShares {
  const sum = Math.max(1e-12, a + b + c + d);
  return {
    spaceHeating: clamp01(a / sum),
    waterHeating: clamp01(b / sum),
    cooking: clamp01(c / sum),
    other: clamp01(d / sum),
  };
}

export function normalizeResidentialGasEndUse(
  partial?: Partial<ResidentialGasEndUseShares> | null
): ResidentialGasEndUseShares {
  const p = partial ?? {};
  return normalize4(
    p.spaceHeating ?? DEFAULT_RESIDENTIAL_GAS_END_USE.spaceHeating,
    p.waterHeating ?? DEFAULT_RESIDENTIAL_GAS_END_USE.waterHeating,
    p.cooking ?? DEFAULT_RESIDENTIAL_GAS_END_USE.cooking,
    p.clothesDrying ?? DEFAULT_RESIDENTIAL_GAS_END_USE.clothesDrying
  );
}

export function normalizeCommercialGasEndUse(
  partial?: Partial<CommercialGasEndUseShares> | null
): CommercialGasEndUseShares {
  const p = partial ?? {};
  return normalizeCommercial4(
    p.spaceHeating ?? DEFAULT_COMMERCIAL_GAS_END_USE.spaceHeating,
    p.waterHeating ?? DEFAULT_COMMERCIAL_GAS_END_USE.waterHeating,
    p.cooking ?? DEFAULT_COMMERCIAL_GAS_END_USE.cooking,
    p.other ?? DEFAULT_COMMERCIAL_GAS_END_USE.other
  );
}

export function getDefaultBuildingEndUseAssumptions(): BuildingEndUseAssumptions {
  return {
    residential: { ...DEFAULT_RESIDENTIAL_GAS_END_USE },
    commercial: { ...DEFAULT_COMMERCIAL_GAS_END_USE },
  };
}

/**
 * Merge scenario field with defaults and normalize each sector to sum to 1.
 */
export function resolveBuildingEndUseAssumptions(
  scenario?: { buildingEndUseAssumptions?: BuildingEndUseAssumptionsInput | null } | null
): ResolvedBuildingEndUseAssumptions {
  const raw = scenario?.buildingEndUseAssumptions;
  const residential = normalizeResidentialGasEndUse(raw?.residential);
  const commercial = normalizeCommercialGasEndUse(raw?.commercial);
  return {
    residential,
    commercial,
    electrifiableResidential: clamp01(residential.spaceHeating + residential.waterHeating),
    electrifiableCommercial: clamp01(commercial.spaceHeating + commercial.waterHeating),
  };
}

/**
 * After efficiency layer, split total therms into res/com using BAU sector shares, then sum only electrifiable (space+water) gas.
 */
export function electrifiableGasThermsInEfficiencySlice(
  efficiencyTotalTherms: number,
  bauTotalTherms: number,
  bauResTherms: number,
  resolved: ResolvedBuildingEndUseAssumptions
): number {
  const resFrac = bauTotalTherms > 0 ? bauResTherms / bauTotalTherms : 0.6;
  const effRes = efficiencyTotalTherms * resFrac;
  const effCom = efficiencyTotalTherms * (1 - resFrac);
  return Math.max(
    0,
    effRes * resolved.electrifiableResidential + effCom * resolved.electrifiableCommercial
  );
}
